import { Injectable, inject } from '@angular/core';
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { first, map, catchError, tap, switchMap, take, merge, Observable, of } from 'rxjs';
import { UserService } from 'src/app/core/services/user.service';
import { EppReadyFlowStepsEnum } from 'src/app/early-pay-requests/models/epp-ready.model';

import { AuthService } from '../../auth/auth.service';
import { ONBOARDING_ROUTES } from '../../cfo/constants/cfo-routes.constants';
import { CustomizableBannerSpotNamesEnum } from '../../models/customizable-banner';
import { CustomizableBannerService } from '../services/customizable-banner.service';
import { PlatformConfirmationService } from '../services/platform-confirmation.service';
import { SnackbarService } from '../services/snackbar.service';
import { formatError } from '../utils';

@Injectable({ providedIn: 'root' })
export class AuthGuard {
  private router = inject(Router);
  private userService = inject(UserService);
  private authService = inject(AuthService);
  private customizableBannerService = inject(CustomizableBannerService);
  private snackbarService = inject(SnackbarService);
  private platformConfirmationService = inject(PlatformConfirmationService);

  constructor() {
    const user = this.userService.getUser();
    this.authService.identifyUser(user);
  }

  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean | UrlTree> {
    const { code, ...rest } = route.queryParams;
    if (code) {
      return this.userService.fetchUserByCode(code).pipe(
        map(() => {
          this.router.navigate([location.pathname], { queryParams: rest });
          return true;
        }),
        catchError((error) => {
          const snackBarRef = this.snackbarService.error(formatError(error.error));
          return merge(snackBarRef.onAction(), snackBarRef.afterDismissed()).pipe(
            take(1),
            tap(() => {
              this.router.navigate(['/']);
            }),
            switchMap(() => of(false)),
          );
        }),
      );
    }

    const currentUser = this.userService.getUser();
    const isUserCompanySc = this.userService.isUserCompanySc();
    const isUserRegisteredFromEppReadyFlowAndNotFinished =
      currentUser?.is_registered_from_epp_ready_flow &&
      currentUser?.epp_ready_flow_current_step !== EppReadyFlowStepsEnum.FLOW_SUCCESSFUL;
    const isUserPositionMissing = !currentUser?.position;
    const isUserNameMissing = !currentUser?.first_name || !currentUser?.last_name;
    if (!currentUser) {
      if (state.url.includes('contracts/view-contract')) {
        this.authService.setContractLink(state.url);
      }
      this.router.navigate(['auth', 'login'], { queryParams: { redirect: state.url } });
      return of(false);
    }

    this.authService.syncSourceResourceAttribution(currentUser);

    if (state.url.includes('pre-approval/role-selector')) {
      return of(true);
    }

    if (this.authService.getContractLink()) {
      this.authService.navigateToContractAndClearLink();
      return of(true);
    }

    const isCruOnboardingRoute = state.url.startsWith(ONBOARDING_ROUTES.BASE);
    const shouldRedirectToCruOnboarding = this.authService.shouldRedirectToCruOnboarding(currentUser);
    const isCruOnboardingInProgress = isCruOnboardingRoute || shouldRedirectToCruOnboarding;

    if (isUserCompanySc && isUserRegisteredFromEppReadyFlowAndNotFinished && !isCruOnboardingInProgress) {
      setTimeout(() => {
        this.router.navigate(['early-pay']);
        return true;
      });
    }

    if (
      currentUser.company_type !== 'lender' &&
      !isUserRegisteredFromEppReadyFlowAndNotFinished &&
      !currentUser.login_as_session
    ) {
      this.redirectToPromoPageIfRequired();
    }
    const areUserResponsibilitiesMissing = isUserCompanySc && !currentUser.responsibilities?.length;
    const needsRoleSelector =
      !currentUser.login_as_session &&
      !isUserRegisteredFromEppReadyFlowAndNotFinished &&
      (isUserNameMissing || (isUserCompanySc && isUserPositionMissing) || areUserResponsibilitiesMissing);
    if (needsRoleSelector) {
      if (state.url.includes('contracts/view-contract')) {
        this.authService.setContractLink(state.url);
      }
      return of(this.router.createUrlTree(['pre-approval', 'role-selector']));
    }

    if (!isCruOnboardingRoute && shouldRedirectToCruOnboarding) {
      return of(this.router.createUrlTree([ONBOARDING_ROUTES.BASE]));
    }

    if (
      isUserCompanySc &&
      !isUserRegisteredFromEppReadyFlowAndNotFinished &&
      currentUser.is_principal === null &&
      !currentUser.login_as_session
    ) {
      if (state.url.includes('contracts/view-contract')) {
        this.authService.setContractLink(state.url);
      }
    }

    this.platformConfirmationService.enforceForPriorSigners();

    return of(true);
  }

  canActivateChild(childRoute: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean | UrlTree> {
    return this.canActivate(childRoute, state);
  }

  private redirectToPromoPageIfRequired(): void {
    this.customizableBannerService
      .getCustomizableBanner(CustomizableBannerSpotNamesEnum.FULL_PAGE_PROMO_BANNER)
      .pipe(first())
      .subscribe((banner) => {
        if (banner) {
          setTimeout(() => {
            this.router.navigate(['full-page-promo-banner']);
          });
        }
      });
  }
}
